/* eslint-disable quotes */
import { observer } from 'mobx-react-lite'
import { withZod } from '@remix-validated-form/with-zod'
import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { EditPencil, Plus, Trash } from 'iconoir-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { DateComponent } from 'app/components/DateComponent'
import FormHiddenInput from 'app/components/form/FormHiddenInput'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import { Modal } from 'app/components/Modal'
import { PageScroll } from 'app/components/layout/PageScroll'
import { TagColorPicker } from 'app/components/tags/TagColorPicker'
import { tagColorNames, tagSwatchClasses } from 'app/lib/chipColors'
import { Button } from 'app/components/ui/button'
import { DialogActionBar } from 'app/components/ui/dialog'
import { ActionsCell } from 'app/components/table/ActionsCell'
import SimpleTable from 'app/components/table/SimpleTable'
import { Tooltip } from 'app/components/Tooltip'
import { FilterValueType, FilterableColumn } from 'app/filter.utils'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'
import { TagColorType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import {
  getBuiltInTags,
  getContactBuiltInTags,
  getActivityBuiltInTags,
  getContactBuiltInTagColor,
  getActivityBuiltInTagColor,
} from 'lib/getBuiltInTags'
import { logError } from 'lib/logging'
import { useMutation } from 'urql'
import { graphql } from 'app/generated/gql'
import { gqlFetchList } from 'app/lib/graphqlClient'

const DELETE_TAG_COLOR_MUTATION = graphql(`
  mutation DeleteTagColor($id: ID!) {
    deleteTagColor(id: $id)
  }
`)

// deleteTagColor returns a bare Boolean, so urql's document cache finds no typename to
// invalidate and serves the just-deleted tag from cache when we refetch. Declaring the
// typename in the mutation context drops every cached TagColor query, here and in the
// tag pickers.
const DELETE_CONTEXT = { additionalTypenames: [`TagColor`] }

const CREATE_TAG_COLOR_MUTATION = graphql(`
  mutation SettingsCreateTagColor($input: CreateTagColorInput!) {
    createTagColor(input: $input) {
      id
      name
      color
      entity
    }
  }
`)

const UPDATE_TAG_COLOR_MUTATION = graphql(`
  mutation UpdateTagColor($id: ID!, $color: String!) {
    updateTagColor(id: $id, color: $color) {
      id
      name
      color
    }
  }
`)

enum Entity {
  locations = `Location`,
  workspaces = `Workspace`,
  contacts = `Contact`,
  activities = `Activity`,
}
const tagEntitySchema = z.nativeEnum(Entity)

const createSchema = z.object({
  orgId: z.string().min(1, { message: `An organization is required` }),
  workspaceId: zfd.text(z.string().optional()),
  entity: tagEntitySchema,
  name: z.string().min(1, { message: `A name is required` }),
  color: z.string().min(1, { message: `Tag Color is required` }),
})

export const createValidator = withZod(createSchema)

const editSchema = z.object({
  id: z.string().min(1, { message: `An ID is required` }),
  orgId: z.string().min(1, { message: `An organization is required` }),
  workspaceId: zfd.text(z.string().optional()),
  entity: tagEntitySchema,
  prevName: z.string(),
  name: z.string().min(1, { message: `A name is required` }),
  color: z.string().min(2, { message: `Tag Color is required` }),
})

const editValidator = withZod(editSchema)

export { presetColors } from 'app/utils/presetColors'
import { presetColors } from 'app/utils/presetColors'

const filterableColumns: FilterableColumn[] = [{ attribute: `name`, displayName: `Name`, type: FilterValueType.string }]

// observer: reads the staff-v2 feature toggle, authStore.user and the workspace store in
// render; these resolve asynchronously / change in place, so TagsTable needs an observer
// wrapper (TRK-6020).
const TagsTable = observer(function TagsTable() {
  const { entity } = useParams<{ entity: string }>()
  const [data, setData] = useState<{ hits: TagColorType[] }>()
  const [actionData, setActionData] = useState<{ success?: boolean; message?: string }>()
  const user = useUser()
  const workspace = useWorkspace()
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()
  const [, deleteTagColor] = useMutation(DELETE_TAG_COLOR_MUTATION)
  const [, createTagColor] = useMutation(CREATE_TAG_COLOR_MUTATION)
  const [, updateTagColor] = useMutation(UPDATE_TAG_COLOR_MUTATION)

  const fetchData = useCallback(() => {
    if (!entity) return
    // Org users manage org-level tags; workspace users are scoped by the API via user.workspaceId.
    const orgOnly = !user?.workspaceId ? ` AND isOrgTag:true` : ``
    gqlFetchList<TagColorType>(
      'tagColors',
      { search: `entity:${tagEntitySchema.parse(Entity[entity!])}${orgOnly}` },
      'id name color entity lastUsedDate orgId workspaceId',
    )
      .then((d) => {
        const entityValue = tagEntitySchema.parse(Entity[entity!])
        // Merge built-in tags that don't exist as TagColor records (workspace view only)
        const inWorkspaceView = !!(user?.workspaceId || workspace)
        if (inWorkspaceView && (entityValue === `Contact` || entityValue === `Activity`)) {
          const builtIns = entityValue === `Contact` ? getContactBuiltInTags() : getActivityBuiltInTags()
          const getColor = entityValue === `Contact` ? getContactBuiltInTagColor : getActivityBuiltInTagColor
          const existingNames = new Set(d.hits.map((t) => t.name))
          const missingBuiltIns: TagColorType[] = builtIns
            .filter((name) => !existingNames.has(name))
            .map((name) => ({ id: name, name, color: getColor(name), entity: entityValue }) as TagColorType)
          setData({ hits: [...d.hits, ...missingBuiltIns] })
        } else {
          setData({ hits: d.hits })
        }
      })
      .catch((err) => logError(`Error fetching tags`, err))
  }, [entity, user?.workspaceId, workspace])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const submitAction = useCallback(
    (formData: FormData) => {
      if (!entity) return
      const subAction = formData.get(`subAction`) as string
      const entityValue = tagEntitySchema.parse(Entity[entity!])

      if (subAction === `delete`) {
        const id = formData.get(`id`) as string
        deleteTagColor({ id }, DELETE_CONTEXT)
          .then((result) => {
            if (result.error) {
              setActionData({ success: false, message: result.error.message })
            } else {
              setActionData({ success: true, message: `Tag deleted` })
              fetchData()
            }
          })
          .catch((err) => logError(`Error deleting tag`, err))
      } else if (subAction === `create`) {
        const input = {
          entity: entityValue,
          name: formData.get(`name`) as string,
          color: formData.get(`color`) as string,
        }
        createTagColor({ input })
          .then((result) => {
            if (result.error) {
              setActionData({ success: false, message: result.error.message })
            } else {
              setActionData({ success: true, message: `Tag created` })
              fetchData()
            }
          })
          .catch((err) => logError(`Error creating tag`, err))
      } else if (subAction === `edit`) {
        const id = formData.get(`id`) as string
        const prevName = formData.get(`prevName`) as string
        const name = formData.get(`name`) as string
        const color = formData.get(`color`) as string
        const nameChanged = prevName !== name

        const ops = nameChanged
          ? Promise.all([
              deleteTagColor({ id }, DELETE_CONTEXT),
              createTagColor({ input: { entity: entityValue, name, color } }),
            ]).then(([del, create]) => create)
          : updateTagColor({ id, color })

        ops
          .then((result) => {
            if (result.error) {
              setActionData({ success: false, message: result.error.message })
            } else {
              setActionData({ success: true, message: `Tag updated` })
              fetchData()
            }
          })
          .catch((err) => logError(`Error updating tag`, err))
      }
    },
    [entity, fetchData, deleteTagColor, createTagColor, updateTagColor],
  )

  const [searchValue, setSearchValue] = useState<string>()
  const tagsForEntity = useMemo(
    () => (data?.hits?.filter((tag) => tag.entity === tagEntitySchema.parse(Entity[entity!])) ?? []) as TagColorType[],
    [data, entity],
  )
  const filteredRows = useClientSideTableFilters(tagsForEntity, filterableColumns, `id`, [`name`], searchValue)
  const [open, setOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState<string>(presetColors[0])
  const [isEditing, setIsEditing] = useState(false)
  const [editingTag, setEditingTag] = useState<TagColorType>()
  const [nameDraft, setNameDraft] = useState(``)

  const handleCloseModal = () => {
    setOpen(false)
    setEditingTag(undefined)
    setSelectedColor(presetColors[0])
    setNameDraft(``)
  }

  useEffect(() => {
    if (actionData?.success) {
      notifySuccess(actionData.message ?? ``)
      handleCloseModal()
    } else if (actionData?.success === false) {
      notifyError(actionData.message ?? ``)
    }
  }, [actionData])

  if (!user) return null

  const canEditTags = checkPermission(`settings:tags:edit`)

  const handleEditTag = (tag: TagColorType) => {
    setIsEditing(true)
    setEditingTag(tag)
    setSelectedColor(tag.color!)
    setNameDraft(tag.name ?? ``)
    setOpen(true)
  }

  const handleDeleteTag = async (tagId: string) => {
    await confirm({
      title: `Delete Tag`,
      description: `Are you sure you want to delete this tag?`,
      variant: `danger`,
    })
    const deleteFormData = new FormData()
    deleteFormData.append(`subAction`, `delete`)
    deleteFormData.append(`id`, tagId)
    submitAction(deleteFormData)
  }

  const defaultColumns: ColumnDef<TagColorType, any>[] = [
    {
      accessorFn: (row) => row.name,
      id: `Tag Name`,
      header: `Tag Name`,
      minSize: 100,
      meta: {
        defaultVisible: true,
        pinned: true,
        getCellStyle: () => `h-12`,
        ControlledComponent: (row: TagColorType | undefined) =>
          row == null ? (
            <></>
          ) : (
            <ActionsCell
              content={row?.name ?? ''}
              actions={
                canEditTags ? (
                  <div className="flex items-center gap-x-1">
                    <Tooltip message="Edit tag" className="!cursor-pointer">
                      <EditPencil className="text-strong" onClick={() => handleEditTag(row)} />
                    </Tooltip>
                    {getBuiltInTags().includes(row?.name!) ? (
                      <Tooltip message="System tags cannot be deleted" className="!cursor-pointer text-strong">
                        <Trash className="text-strong" />
                      </Tooltip>
                    ) : (
                      <Tooltip message="Delete tag" className="!cursor-pointer text-strong">
                        <Trash className="text-strong" onClick={() => handleDeleteTag(row?.id!)} />
                      </Tooltip>
                    )}
                  </div>
                ) : undefined
              }
            />
          ),
      },
      enableHiding: false,
    },
    {
      accessorFn: (row) => row.color,
      id: `Color`,
      cell: (info) => (
        <span className="flex items-center">
          <div
            className={classNames(
              'h-3.5 w-3.5 rounded-full ring-1 ring-inset ring-strong',
              tagSwatchClasses(info.getValue()),
            )}
          ></div>
          <span className="ml-2 text-body-xs text-default">{tagColorNames[info.getValue()]}</span>
        </span>
      ),
      header: `Color`,
      meta: {
        defaultVisible: true,
      },
    },
    {
      accessorFn: (row) => row.lastUsedDate,
      id: `Last Used`,
      header: `Last Used`,
      cell: (info) => <DateComponent value={info.getValue()} />,
      meta: {
        defaultVisible: true,
      },
    },
  ]

  return (
    <PageScroll variant="table" contentClassName="pt-6">
      <SimpleTable
        data={filteredRows}
        columns={defaultColumns}
        showColumnOrdering={false}
        onSearchValueChange={(val) => setSearchValue(val)}
        hideFooter
        enableExport={false}
        edgePadded
        extraSlot={
          canEditTags ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setIsEditing(false)
                setOpen(true)
              }}
            >
              <Plus className="h-4 w-4" />
              <span>Add New Tag</span>
            </Button>
          ) : undefined
        }
      />
      {/* eslint-disable-next-line deprecation/deprecation */}
      <Modal
        open={open}
        title={isEditing ? `Edit tag` : `Add new tag`}
        isFullScreen={false}
        removePadding
        onClose={() => handleCloseModal()}
        children={
          <>
            <div className="w-screen md:w-120">
              <SmartForm
                className="mb-0 mt-0 flex flex-col gap-y-0"
                hideButtons
                defaultValues={isEditing && editingTag ? { name: editingTag.name } : undefined}
                replace
                validator={isEditing ? editValidator : createValidator}
                method="POST"
                preventSubmit
                onSubmit={(data) => {
                  if (!data) return
                  const formData = new FormData()
                  formData.append('subAction', isEditing ? 'edit' : 'create')
                  Object.entries(data).forEach(([key, value]) => {
                    if (value != null) formData.append(key, String(value))
                  })
                  submitAction(formData)
                }}
              >
                <FormHiddenInput name="subAction" value={isEditing ? `edit` : `create`} />
                {isEditing && editingTag ? <FormHiddenInput name="id" value={editingTag.id!} /> : null}
                {isEditing && editingTag ? <FormHiddenInput name="prevName" value={editingTag.name!} /> : null}
                <FormHiddenInput name="orgId" value={workspace?.orgId ?? user.orgId} />
                <FormHiddenInput name="workspaceId" value={workspace?.id} />
                <FormHiddenInput name="entity" value={tagEntitySchema.parse(Entity[entity!])} />
                <FormHiddenInput name="color" value={selectedColor} cols={6} />
                <div className="col-span-6 flex flex-col gap-6 px-6 py-5">
                  <Tooltip
                    className="col-span-12 md:col-span-6"
                    message={
                      editingTag && getBuiltInTags().includes(editingTag.name!)
                        ? 'System tags cannot be renamed'
                        : undefined
                    }
                  >
                    <FormInput
                      name="name"
                      label="Tag Name"
                      labelClassName="text-body-sm text-strong"
                      cols={6}
                      disabled={editingTag && getBuiltInTags().includes(editingTag.name!)}
                      onChange={(e) => setNameDraft(e.target.value)}
                    />
                  </Tooltip>
                  <div>
                    <label className="text-body-sm text-strong">Tag Color</label>
                    <TagColorPicker
                      className="mt-2"
                      value={selectedColor}
                      onChange={setSelectedColor}
                      previewName={nameDraft}
                    />
                  </div>
                </div>
                <DialogActionBar className="col-span-6">
                  <Button type="button" variant="secondary" size="md" onClick={() => handleCloseModal()}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary" size="md">
                    Save
                  </Button>
                </DialogActionBar>
              </SmartForm>
            </div>
          </>
        }
      />
    </PageScroll>
  )
})

export default TagsTable
